import settings from '../../../devices/all/settings';
import Device from "../../../devices/device";
import Utils from "../../Utils";
import DeviceStorage from '../../../components/DeviceStorage/DeviceStorage';
import {setLastFlush} from "../../../actions/metrics";
import store from "../../../store";

/** Clave bajo la que se persiste el client_id generado para GA4. */
const GA4_CLIENT_ID_KEY = '_ga4_client_id';

const GA4_MP_ENDPOINT = 'https://www.google-analytics.com/mp/collect';

/** Umbral de flush: iniciar el envío secuencial cuando haya esta cantidad de payloads en cola. */
const FLUSH_THRESHOLD = 10;

/** Intervalo del temporizador en milisegundos antes de vaciar una cola pendiente. */
const FLUSH_INTERVAL_MS = 5000;

/**
 * Retrasos de reintento en milisegundos para un POST fallido por payload.
 * Índice 0 → primer reintento, índice 1 → segundo reintento, índice 2 → tercer reintento.
 */
const RETRY_DELAYS_MS = [1000, 2000, 4000];

/**
 * Cliente HTTP para el Measurement Protocol REST de GA4.
 * Contiene la lógica compartida entre todos los transformers:
 * detección de PS4, lectura de cookie de sesión y envío del payload.
 *
 * Los payloads se almacenan en memoria y se entregan de forma secuencial.
 * El flush se dispara al llegar a FLUSH_THRESHOLD payloads o después de
 * FLUSH_INTERVAL_MS milisegundos, lo que ocurra primero. Cada payload se
 * reintenta hasta RETRY_DELAYS_MS.length veces antes de descartarse.
 */
export class GA4RestClient {

    /**
     * Determina si el dispositivo actual es una PS4 inspeccionando el user agent.
     *
     * @returns {boolean}
     */
    static isPS4() {
        return Utils.isPlaystation4();
    }

    /**
     * Retorna el session_id actual como entero.
     *
     * GA4 Measurement Protocol requiere que session_id sea un entero (timestamp
     * Unix en segundos del inicio de sesión). La cookie _ga_XXXXX tiene el
     * formato "GS2.1.s{session_start_ts}$o{...}" y se extrae el segmento
     * numérico. Si la cookie no existe o no puede interpretarse, se genera un
     * timestamp de inicio de módulo y se persiste en memoria.
     *
     * @returns {number}
     */
    static getSessionId() {
        const rawCookie = Utils.getCookie('_ga_ui');
        if (rawCookie) {
            const match = rawCookie.match(/\.s(\d+)/);
            if (match) return Number(match[1]);
        }
        if (!GA4RestClient._sessionId) {
            GA4RestClient._sessionId = Math.floor(Date.now() / 1000);
        }
        return GA4RestClient._sessionId;
    }

    /**
     * Retorna un client_id válido y único para GA4.
     *
     * Orden de resolución:
     * 1. Cookie estándar _ga (formato "GA1.X.XXXXXXXXXX.XXXXXXXXXX")
     * 2. Valor específico de GA4 persistido en DeviceStorage
     * 3. Fallback externo si no está vacío, no es "anonymous" y no es hex de 32 caracteres
     * 4. Valor generado "{random}.{timestamp_seconds}" persistido en DeviceStorage
     *
     * @param {string} [fallback]  Valor candidato (por ejemplo dimension2 o user_id).
     * @returns {string}
     */
    static getClientId(fallback) {
        const gaCookie = Utils.getCookie('_ga');
        if (gaCookie) {
            const parts = gaCookie.split('.');
            if (parts.length >= 4) {
                return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
            }
        }

        const storedGa4Id = DeviceStorage.getItem(GA4_CLIENT_ID_KEY);
        if (storedGa4Id) return storedGa4Id;

        const HEX32 = /^[0-9a-f]{32}$/i;
        if (fallback && fallback !== 'anonymous' && !HEX32.test(fallback)) return fallback;

        const generated = String(Math.floor(Math.random() * 2147483647)) + '.' + Math.floor(Date.now() / 1000);
        try {
            DeviceStorage.setItem(GA4_CLIENT_ID_KEY, generated);
        } catch (e) { /* best-effort */ }
        return generated;
    }

    /**
     * Encola un payload de GA4 para su envío. Dispara un flush por umbral cuando
     * la cola alcanza FLUSH_THRESHOLD, o programa un flush por temporizador en caso contrario.
     *
     * La firma pública `send(payload)` no cambia.
     *
     * @param {object} payload  Payload in GA4 Measurement Protocol format.
     * @returns {void}
     */
    static send(payload) {
        GA4RestClient._queue.push(payload);

        if (GA4RestClient._queue.length >= FLUSH_THRESHOLD) {
            GA4RestClient._cancelFlushTimer();
            GA4RestClient.flush();
        } else {
            GA4RestClient._ensureFlushTimer();
        }
    }

    /**
     * Programa un flush por temporizador si aún no hay uno activo.
     * @private
     */
    static _ensureFlushTimer() {
        if (GA4RestClient._flushTimer !== null) return;
        GA4RestClient._flushTimer = setTimeout(function () {
            GA4RestClient._flushTimer = null;
            GA4RestClient.flush();
        }, FLUSH_INTERVAL_MS);
    }

    /**
     * Cancela cualquier temporizador de flush pendiente.
     * @private
     */
    static _cancelFlushTimer() {
        if (GA4RestClient._flushTimer !== null) {
            clearTimeout(GA4RestClient._flushTimer);
            GA4RestClient._flushTimer = null;
        }
    }

    /**
     * Vacía la cola actual y envía cada payload de forma secuencial.
     * No hace nada si ya hay un flush en curso o si la cola está vacía.
     */
    /**
     * Establece el user_id que se incluirá en el root del POST de GA4 MP.
     * Se actualiza con cada evento; en PS4 el usuario no cambia mid-sesión.
     *
     * @param {string} id
     */
    static setUserId(id) {
        if (id) GA4RestClient._userId = id;
    }

    /**
     * Vacía la cola actual y envía los eventos acumulados en un solo POST.
     * Si la cola supera 25 eventos (límite GA4 MP), los parte en chunks y los
     * envía secuencialmente. No hace nada si ya hay un flush en curso.
     */
    static flush() {
        if (GA4RestClient._queue.length === 0) return;
        if (GA4RestClient._isFlushing) return;

        var batch = GA4RestClient._queue.slice();
        GA4RestClient._queue = [];
        GA4RestClient._isFlushing = true;

        GA4RestClient._sendChunks(batch, 0, function () {
            GA4RestClient._isFlushing = false;
        });

      store.dispatch(setLastFlush(Date.now()));

    }

    /**
     * Ensambla el payload GA4 MP con los eventos del chunk y lo envía.
     * Continúa con el siguiente chunk hasta agotar el lote.
     *
     * @param {Array<object>} events  Eventos { name, params } a enviar.
     * @param {number}        offset  Índice de inicio del chunk actual.
     * @param {Function}      done    Se invoca cuando todos los chunks terminan.
     * @private
     */
    static _sendChunks(events, offset, done) {
        if (offset >= events.length) {
            done();
            return;
        }

        var chunk = events.slice(offset, offset + 25);
        var payload = {
            client_id: GA4RestClient.getClientId(),
            events: chunk,
        };
        var resolvedUserId = GA4RestClient._userId || DeviceStorage.getItem('user_id');
        if (resolvedUserId) {
            payload.user_id = resolvedUserId;
        }

        GA4RestClient._sendWithRetry(payload, 0, function () {
            GA4RestClient._sendChunks(events, offset + 25, done);
        });
    }

    /**
     * Envía un solo payload mediante XHR POST. Si falla, reintenta hasta
     * RETRY_DELAYS_MS.length veces. Después del fallo final, registra el error
     * y continúa con el siguiente payload usando el callback `done`.
     *
     * @param {object}   payload   Payload de GA4 a enviar.
     * @param {number}   retryIdx  Índice del intento actual (0 = envío inicial).
     * @param {Function} done      Se invoca cuando el payload termina.
     * @private
     */
    static _sendWithRetry(payload, retryIdx, done) {
        GA4RestClient._postPayload(payload, function (success) {
            if (success) {
                done();
                return;
            }

            if (retryIdx < RETRY_DELAYS_MS.length) {
                var delay = RETRY_DELAYS_MS[retryIdx];
                setTimeout(function () {
                    GA4RestClient._sendWithRetry(payload, retryIdx + 1, done);
                }, delay);
            } else {
                console.error('[GA4RestClient] Payload discarded after ' + RETRY_DELAYS_MS.length + ' retries.');
                done();
            }
        });
    }

    /**
     * Ejecuta un único POST por XHR para un payload de GA4. Llama a
     * `callback(true)` en HTTP 2xx y `callback(false)` en cualquier fallo o estado no 2xx.
     *
     * Usa Content-Type: text/plain para evitar el preflight de CORS, ya que
     * el endpoint /mp/collect no soporta peticiones OPTIONS.
     *
     * @param {object}   payload   Payload de GA4 Measurement Protocol.
     * @param {Function} callback  Recibe `true` en éxito y `false` en fallo.
     * @private
     */
    static _postPayload(payload, callback) {
        const ga4Settings = settings.ga4 || {};
        const measurementId = ga4Settings.measurement_id;
        const apiSecret = ga4Settings.api_secret;

        if (!measurementId || !apiSecret) {
            console.warn('[GA4RestClient] measurement_id o api_secret no configurados en settings.ga4');
            callback(false);
            return;
        }

        const url = `${GA4_MP_ENDPOINT}?measurement_id=${encodeURIComponent(measurementId)}&api_secret=${encodeURIComponent(apiSecret)}`;

        try {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', url, true);
            xhr.setRequestHeader('Content-Type', 'text/plain');

            xhr.onreadystatechange = function () {
                if (xhr.readyState !== 4) return;
                callback(xhr.status >= 200 && xhr.status < 300);
            };

            xhr.onerror = function () {
                console.error('[GA4RestClient] network error sending payload');
                callback(false);
            };

            xhr.send(JSON.stringify(payload));
        } catch (e) {
            console.error('[GA4RestClient] error sending payload', e);
            callback(false);
        }
    }
}

// ---------------------------------------------------------------------------
// Estado estático: una cola por clase, compartida entre todos los llamados.
// Reiniciar estos valores en tests mediante `beforeEach` para asegurar aislamiento.
// ---------------------------------------------------------------------------

/**
 * Cola de payloads en memoria.
 * @type {Array<object>}
 */
GA4RestClient._queue = [];

/**
 * Referencia activa del temporizador de flush, o null cuando no hay temporizador programado.
 * @type {number|null}
 */
GA4RestClient._flushTimer = null;

/**
 * Bandera de control para evitar flushes secuenciales superpuestos.
 * @type {boolean}
 */
GA4RestClient._isFlushing = false;

/**
 * session_id de respaldo generado cuando falta la cookie _ga_ui.
 * Se inicializa en la primera llamada a getSessionId().
 * @type {number|undefined}
 */
GA4RestClient._sessionId = undefined;

/**
 * user_id del último evento encolado. Se incluye en el root del POST de GA4 MP.
 * @type {string|null}
 */
GA4RestClient._userId = null;



// WEBPACK FOOTER //
// ./src/utils/trackers/ga-api-rest-ps4/GA4RestClient.js